import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';

/* ─── Types ─────────────────────────────────────────────────────── */

export type ReportStatus =
	| 'collecting'
	| 'ready_for_review'
	| 'in_review'
	| 'approved'
	| 'sent'
	| 'send_failed';

export type ReportFlag =
	| 'no_photos'
	| 'no_outcome'
	| 'not_submitted'
	| 'low_attendance'
	| 'missed';

export interface ReportTotals {
	classes: number;
	held: number;
	missed: number;
	cancelled: number;
	attendancePct: number | null;
	photoCount: number;
}

export interface DispatchRow {
	id: string;
	school: { id: string; name: string };
	status: ReportStatus;
	totals: ReportTotals;
	flags: ReportFlag[];
	entryCount: number;
	delivery: {
		emailOk: boolean | null;
		whatsappOk: boolean | null;
		firstViewedAt: string | null;
		publicUrl: string;
		pdfUrl: string;
	};
	approvedAt: string | null;
}

export interface DispatchBoard {
	date: string;
	/** School-local hour reports are built at — comes from the server so the UI
	 *  never states a cutoff that has drifted from the actual setting. */
	cutoffHour: number;
	totals: { schools: number; sent: number; pending: number };
	reports: DispatchRow[];
}

export interface ReportEntryPhoto {
	captureId: string;
	url: string;
	includedInReport: boolean;
}

export interface ReportEntry {
	id: string;
	timetableSlot: string;
	classLabels: string[];
	trainerName: string;
	timeLabel: string;
	slotStatus: string;
	curriculum: {
		curriculumTitle: string;
		moduleTitle: string;
		sessionTitle: string;
	};
	outcome: string;
	outcomeEdited: boolean;
	trainerRemark: string;
	adminRemark: string;
	missedReasonLabel: string | null;
	attendance: { present: number; total: number; pct: number | null };
	photos: ReportEntryPhoto[];
	flags: ReportFlag[];
}

export interface DailyReportDetail {
	id: string;
	school: { id: string; fullName: string; city: string; logoUrl: string; email: string };
	dayBucket: string;
	tz: string;
	status: ReportStatus;
	entries: ReportEntry[];
	totals: ReportTotals;
	delivery: {
		publicToken: string;
		publicUrl: string;
		pdfUrl: string;
		email: { sentAt: string | null; to: string; ok: boolean | null; error: string };
		whatsapp: { sentAt: string | null; to: string[]; ok: boolean | null; error: string };
		firstViewedAt: string | null;
	};
	approvedAt: string | null;
}

/* ─── API ───────────────────────────────────────────────────────── */

export type RecipientTarget = 'school_admin' | 'zonal_manager' | 'both';

/**
 * Report endpoints exist under two prefixes serving the same controller: Master
 * Admin sees every school, a Zonal Manager only their own. Deriving the prefix
 * from the stored role keeps one API slice and one review page rather than a
 * near-identical copy per role.
 */
const reportBase = () =>
	localStorage.getItem('alforaa_role') === 'zonal_manager'
		? '/zonal'
		: '/master-admin';

export const dailyReportApi = createApi({
	reducerPath: 'dailyReportApi',
	baseQuery: axiosBaseQuery,
	tagTypes: ['DispatchBoard', 'ReportDetail'],
	endpoints: (builder) => ({
		getDispatchBoard: builder.query<DispatchBoard, { date?: string }>({
			query: ({ date }) => ({
				url: `${reportBase()}/daily-reports`,
				params: date ? { date } : undefined,
			}),
			providesTags: ['DispatchBoard'],
		}),
		getDailyReport: builder.query<DailyReportDetail, string>({
			query: (id) => ({ url: `${reportBase()}/daily-reports/${id}` }),
			providesTags: ['ReportDetail'],
		}),
		updateReportEntry: builder.mutation<
			{ ok: boolean; entry: ReportEntry },
			{
				id: string;
				entryId: string;
				outcome?: string;
				adminRemark?: string;
				photos?: { captureId: string; includedInReport: boolean }[];
			}
		>({
			query: ({ id, entryId, ...body }) => ({
				url: `${reportBase()}/daily-reports/${id}/entries/${entryId}`,
				method: 'PATCH',
				data: body,
			}),
			// No invalidation: edits autosave on blur while the reviewer is still
			// working elsewhere on the page, and a refetch would discard in-flight
			// changes to other fields.
		}),
		refreshDailyReport: builder.mutation<{ ok: boolean }, string>({
			query: (id) => ({
				url: `${reportBase()}/daily-reports/${id}/refresh`,
				method: 'POST',
			}),
			invalidatesTags: ['ReportDetail', 'DispatchBoard'],
		}),
		approveDailyReport: builder.mutation<{ ok: boolean; status: ReportStatus }, string>({
			query: (id) => ({
				url: `${reportBase()}/daily-reports/${id}/approve`,
				method: 'POST',
			}),
			invalidatesTags: ['ReportDetail', 'DispatchBoard'],
		}),
		// Publishes the report so its public link works, then the manager forwards
		// it into the school's WhatsApp group by hand. WhatsApp Business has no
		// group-send API, so this is the primary delivery path for most schools.
		publishDailyReport: builder.mutation<
			{ ok: boolean; status: ReportStatus; publicUrl: string; pdfUrl: string },
			string
		>({
			query: (id) => ({
				url: `${reportBase()}/daily-reports/${id}/publish`,
				method: 'POST',
			}),
			invalidatesTags: ['ReportDetail', 'DispatchBoard'],
		}),
		sendDailyReport: builder.mutation<
			{
				ok: boolean;
				status: ReportStatus;
				delivery: {
					email: { ok: boolean; error: string };
					whatsapp: { ok: boolean; error: string };
					pdfUrl: string;
					publicUrl: string;
				};
			},
			// §3a: a bare id keeps the historical school-admin behaviour; pass an
			// object to choose who gets the WhatsApp copy.
			string | { id: string; recipientTarget: RecipientTarget }
		>({
			query: (arg) => {
				const id = typeof arg === 'string' ? arg : arg.id;
				return {
					url: `${reportBase()}/daily-reports/${id}/send`,
					method: 'POST',
					data:
						typeof arg === 'string'
							? undefined
							: { recipientTarget: arg.recipientTarget },
				};
			},
			invalidatesTags: ['ReportDetail', 'DispatchBoard'],
		}),
		addReportEntryPhotos: builder.mutation<
			any,
			{ id: string; entryId: string; files: File[] }
		>({
			query: ({ id, entryId, files }) => {
				const form = new FormData();
				files.forEach((f) => form.append('photos', f));
				return {
					url: `${reportBase()}/daily-reports/${id}/entries/${entryId}/photos`,
					method: 'POST',
					data: form,
					// The shared axios client defaults every request to
					// application/json, and axios only picks the multipart type
					// itself when the header is not already set — so without this
					// the upload goes out as JSON with no boundary and multer
					// parses zero files ("No photos provided").
					headers: { 'Content-Type': 'multipart/form-data' },
				};
			},
			invalidatesTags: ['ReportDetail'],
		}),
		nextUnreviewed: builder.query<
			{ next: { id: string; schoolName: string } | null },
			string
		>({
			query: (id) => ({
				url: `${reportBase()}/daily-reports/${id}/next-unreviewed`,
			}),
		}),
	}),
});

export const {
	useGetDispatchBoardQuery,
	useGetDailyReportQuery,
	useUpdateReportEntryMutation,
	useRefreshDailyReportMutation,
	useApproveDailyReportMutation,
	usePublishDailyReportMutation,
	useSendDailyReportMutation,
	useAddReportEntryPhotosMutation,
	useLazyNextUnreviewedQuery,
} = dailyReportApi;
